/**
 * Date Formatting Utilities
 * All dates formatted to DD-MM-YYYY format
 */

/**
 * Formats a date to DD-MM-YYYY format
 * @param {Date|string|Date|null} date - Date to format
 * @param {string} fallback - Fallback text for invalid dates
 * @returns {string} Formatted date string
 */
export function formatDate(date, fallback = '-') {
  if (!date) return fallback;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) return fallback;

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return fallback;
  }
}

/**
 * Formats a date to DD-MM-YYYY HH:MM format (date and time)
 * @param {Date|string|Date|null} date - Date to format
 * @param {string} fallback - Fallback text for invalid dates
 * @returns {string} Formatted date-time string
 */
export function formatDateTime(date, fallback = '-') {
  if (!date) return fallback;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) return fallback;

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('DateTime formatting error:', error);
    return fallback;
  }
}

/**
 * Formats a date to DD-MM-YYYY format for input fields
 * @param {Date|string|Date|null} date - Date to format
 * @returns {string} Formatted date string for HTML input
 */
export function formatDateForInput(date) {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) return '';

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${year}-${month}-${day}`; // HTML date inputs use YYYY-MM-DD
  } catch (error) {
    console.error('Date input formatting error:', error);
    return '';
  }
}

/**
 * Parses a DD-MM-YYYY string to a Date object
 * @param {string} dateString - Date string in DD-MM-YYYY format
 * @returns {Date|null} Date object or null if invalid
 */
export function parseDate(dateString) {
  if (!dateString || typeof dateString !== 'string') return null;

  try {
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);

    // Check if date is valid
    if (isNaN(date.getTime())) return null;

    return date;
  } catch (error) {
    console.error('Date parsing error:', error);
    return null;
  }
}

/**
 * Checks if a date string is in DD-MM-YYYY format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid DD-MM-YYYY format
 */
export function isValidDateFormat(dateString) {
  if (!dateString || typeof dateString !== 'string') return false;

  const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
  const match = dateString.match(regex);

  if (!match) return false;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Basic validation
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1900 || year > 2100) return false;

  return true;
}

/**
 * Gets current date in DD-MM-YYYY format
 * @returns {string} Current date formatted
 */
export function getCurrentDate() {
  return formatDate(new Date());
}

/**
 * Gets current date-time in DD-MM-YYYY HH:MM format
 * @returns {string} Current date-time formatted
 */
export function getCurrentDateTime() {
  return formatDateTime(new Date());
}